// HotX Console 0.1.101 / 2026-09-15 15:23 CST / parent dd160cbd912f26035aa4c8d98e3988a28a0ab6d5 / CDN-PACKAGE-RELEASE-GUARD-20260915.
/**
 * HotX 域名套餐创建请求与错误语义。
 * 功能版本：Console 0.1.100；实际修改时间：2026-09-15 04:28:02 CST；功能源码基线：a9b8b6110efa5c3852b4719e25a0d3d45e99d5f6。
 */
// Console 0.1.101 / 2026-09-15 14:20 CST / parent fbbf070c7ba419eddc96bb73ea3afc37370db1d1.
// CDN-PACKAGE-CHECKOUT-20260915: 草稿、认证和充值返回始终复用同一逻辑申请的幂等键。
import type { DomainCreatePayload } from '../../api/types';
import type { DomainPlanChoice } from './domainPlanEntry';
import type { DomainPackageSelection } from './domainPlanPartitions';

export type LegacyDomainPackageSelection = {
  mode: 'FREE' | 'ADMIN_PUSH';
  planId?: number;
  autoRenew?: boolean;
};

export type DomainPlanCreatePayload = DomainCreatePayload & {
  idempotencyKey: string;
  packageSelection: DomainPackageSelection | LegacyDomainPackageSelection;
};

export interface DomainPlanIntent {
  idempotencyKey: string;
  fingerprint: string;
}

export function resolveDomainPlanIntent(
  existing: DomainPlanIntent | null | undefined,
  fingerprint: string,
  createKey: () => string
): DomainPlanIntent {
  if (existing?.idempotencyKey && existing.fingerprint === fingerprint) return existing;
  return { idempotencyKey: createKey(), fingerprint };
}

export function buildDomainPlanCreatePayload(
  domain: DomainCreatePayload,
  choice: DomainPlanChoice,
  autoRenew: boolean,
  idempotencyKey: string,
  partitionSelection?: DomainPackageSelection
): DomainPlanCreatePayload {
  if (choice.kind === 'PAID' && (!Number.isSafeInteger(choice.planId) || Number(choice.planId) <= 0)) {
    throw new Error('请登录并刷新实时套餐后重新选择。');
  }
  const { applyFreeCdn: _oldFreeFlag, planCode: _oldPlanCode, ...baseDomain } = domain;
  const selection: DomainPackageSelection | LegacyDomainPackageSelection = choice.kind === 'PAID'
    ? partitionSelection?.mode === 'NEW'
      ? { ...partitionSelection, autoRenew }
      : partitionSelection || {
        mode: 'NEW',
        planId: choice.planId as number,
        periodCount: 1,
        autoRenew,
        purchaseConfirmed: true
      }
    : choice.kind === 'FREE'
      ? { mode: 'FREE', autoRenew }
      : { mode: 'ADMIN_PUSH', planId: choice.planId };

  return {
    ...baseDomain,
    ...(choice.kind === 'FREE' ? { applyFreeCdn: true } : {}),
    ...(choice.planCode ? { planCode: choice.planCode } : {}),
    idempotencyKey,
    packageSelection: selection
  };
}

const rechargeableCodes = new Set([
  'INSUFFICIENT_POINTS',
  'POINTS_INSUFFICIENT',
  'POINT_BALANCE_NOT_ENOUGH'
]);

function cleanMessage(value: unknown) {
  if (typeof value !== 'string') return '';
  const message = value.trim();
  if (!message || message.includes('系统繁忙')) return '';
  return /[\u3400-\u9fff]/u.test(message) ? message : '';
}

export function classifyDomainPlanError(error: unknown) {
  const candidate = error as {
    status?: unknown;
    data?: { code?: unknown; message?: unknown; detail?: unknown; error?: unknown; partitionId?: unknown };
    response?: { status?: unknown; data?: { code?: unknown; message?: unknown; detail?: unknown; error?: unknown; partitionId?: unknown } };
    code?: unknown;
    message?: unknown;
  };
  const body = candidate?.response?.data || candidate?.data;
  const code = String(body?.code || candidate?.code || '').trim().toUpperCase();
  const status = Number(candidate?.response?.status || candidate?.status || 0);
  const migrationCapacityInsufficient = [
    'DOMAIN_PARTITION_MOVE_QUOTA_FULL',
    'DOMAIN_PARTITION_MIGRATION_CAPACITY_INSUFFICIENT',
    'DOMAIN_PARTITION_MOVE_CAPACITY_INSUFFICIENT'
  ].includes(code);
  const quotaFull = !migrationCapacityInsufficient
    && (code === 'DOMAIN_PARTITION_QUOTA_FULL' || status === 409 && code.includes('QUOTA'));
  const rechargeRequired = status === 402 || rechargeableCodes.has(code);
  const purchaseSavedDomainFailed = [
    'DOMAIN_PARTITION_PURCHASE_SUCCEEDED_DOMAIN_SAVE_FAILED',
    'DOMAIN_PACKAGE_PURCHASED_DOMAIN_SAVE_FAILED'
  ].includes(code);
  const partitionId = Number(body?.partitionId || 0);
  const retainedPartitionId = purchaseSavedDomainFailed && Number.isSafeInteger(partitionId) && partitionId > 0
    ? partitionId
    : undefined;
  const kind = migrationCapacityInsufficient
    ? 'MIGRATION_CAPACITY_INSUFFICIENT'
    : quotaFull
      ? 'QUOTA_FULL'
      : rechargeRequired
      ? 'BALANCE_INSUFFICIENT'
      : purchaseSavedDomainFailed
        ? 'PURCHASE_SUCCEEDED_DOMAIN_SAVE_FAILED'
        : ['DOMAIN_PARTITION_ROOT_CONFLICT', 'DOMAIN_ROOT_ALREADY_ASSIGNED_TO_OTHER_PARTITION'].includes(code)
            ? 'ROOT_PARTITION_CONFLICT'
            : code === 'DOMAIN_PARTITION_EXPIRED'
              ? 'PARTITION_EXPIRED'
              : code === 'DOMAIN_PARTITION_NOT_FOUND'
                ? 'PARTITION_NOT_FOUND'
                : 'UNKNOWN';
  const fallbackByKind: Record<string, string> = {
    QUOTA_FULL: '已超过套餐域名额度，可以删除不用的域名，或重新购买新套餐。',
    BALANCE_INSUFFICIENT: '积分余额不足，请充值后重新提交。',
    PURCHASE_SUCCEEDED_DOMAIN_SAVE_FAILED: '购买已完成，但域名保存失败。请联系我们处理；再次提交不会重复扣费。',
    MIGRATION_CAPACITY_INSUFFICIENT: '目标套餐分区剩余额度不足，请返回修改。',
    ROOT_PARTITION_CONFLICT: '同一根域名已归属其他分区，请先续费或使用原分区。',
    PARTITION_EXPIRED: '所选套餐分区已到期，请续费后再试。',
    PARTITION_NOT_FOUND: '所选套餐分区不存在，请刷新后重新选择。',
    UNKNOWN: '无法判断，请稍后重试或联系管理员。'
  };
  const message = cleanMessage(body?.message)
    || cleanMessage(body?.detail)
    || cleanMessage(body?.error)
    || cleanMessage(candidate?.message)
    || fallbackByKind[kind];
  return {
    code,
    status,
    kind,
    message,
    quotaFull,
    rechargeRequired,
    contactRequired: purchaseSavedDomainFailed,
    retainedPartitionId,
    returnToEdit: migrationCapacityInsufficient,
    // A response code cannot prove that a new customer intent exists. The view
    // rotates this key only when request identity fields change or after success.
    retryWithNewIntent: false
  };
}

function stableIdentity(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableIdentity);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => [key, stableIdentity(item)])
  );
}

export function domainPlanRequestFingerprint(payload: DomainPlanCreatePayload) {
  const { idempotencyKey: _ignored, ...identity } = payload;
  return JSON.stringify(stableIdentity(identity));
}

export function resolveDomainCreateEndpoint(payload: unknown) {
  const candidate = payload as { packageSelection?: unknown } | null | undefined;
  return candidate?.packageSelection
    ? '/api/v1/console/domain-plans/domains'
    : '/api/v1/domains';
}
